/**
 * PropiedadLocationPanel — panel "Ubicación" del Modal de propiedad.
 * Extraído de propiedades.tsx en round 2026-04-27 (Fase 8) para sacar
 * ~270 LOC del Modal principal y aislar la cascada geo + creación
 * inline de sector.
 *
 * QUÉ HACE
 *   1. Cascada geo País → Región → Comuna → Sector. Cada nivel resetea
 *      los inferiores cuando cambia (mantiene coherencia FK).
 *   2. Crea sector inline dentro del dropdown del Select de sectores
 *      cuando el admin escribe uno nuevo. Valida duplicados local
 *      (case/tilde-insensitive) antes de pegar al API.
 *   3. Campos de dirección física: calle, número, casa/depto, torre,
 *      piso, nro depto/casa, condominio, estacionamiento.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El bloque tenía ~270 LOC en un solo `children:` del Collapse,
 *     muy denso por el dropdownRender del sector con su lógica de
 *     duplicados.
 *   - El componente recibe data ya calculada (paisSelectOptions,
 *     regionSelectOptions, comunaSelectOptions, sectorSelectOptions
 *     memoizados) y los watches del form. Cero cambios de comportamiento.
 *
 * REGLAS DE NEGOCIO RELEVANTES (ver docs/modulo-clientes.md):
 *   - País/región/comuna son catálogos globales (admin-managed).
 *   - Sector es local a una comuna — al crearlo se asocia con la comuna
 *     elegida (FK NOT NULL).
 *   - Si el admin intenta crear sector sin comuna seleccionada, lo
 *     bloqueamos en UI (botón disabled + mensaje warning).
 */

import React from "react";
import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { propiedadesApi } from "../../../services/propiedades.api";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../../utils/antdStatic";

// ─── Tipos auxiliares ───────────────────────────────────────────────────

/** Forma genérica de un option AntD (value/label). */
interface SelectOpt {
  value: number;
  label: string;
}

/** Sector full — incluye foreign key `comuna` para validación de
 *  duplicados local (no pegamos al API si ya existe en la comuna). */
interface SectorRaw {
  id: number;
  comuna: number;
  sector: string;
}

interface PropiedadLocationPanelProps {
  /** Form instance del Modal padre. Lo usamos para `setFieldsValue`
   *  (resetear hijos al cambiar padre, escribir el nuevo sector creado). */
  form: FormInstance;

  /** Opciones memoizadas para los 4 Selects (ya en formato AntD). */
  paisSelectOptions: SelectOpt[];
  regionSelectOptions: SelectOpt[];
  comunaSelectOptions: SelectOpt[];
  sectorSelectOptions: SelectOpt[];

  /** Watches del form padre — controlan placeholders/disabled en
   *  cascada y se usan para validar el panel de creación de sector. */
  watchPaisModal: number | null | undefined;
  watchRegionModal: number | null | undefined;
  watchComunaModal: number | null | undefined;

  /** Mapa id → nombre de comuna para el mensaje contextual del
   *  dropdown ("Se creará dentro de Foo"). Owner del padre. */
  comunaById: Map<number, string>;

  /** Lista de sectores RAW (incluye `comuna` FK) — solo para chequeo
   *  de duplicados local antes del POST. NO usar para renderizar
   *  options del Select (eso viene memoizado en sectorSelectOptions). */
  sectorOptions: SectorRaw[];
  setSectorOptions: React.Dispatch<React.SetStateAction<SectorRaw[]>>;

  /** Estado de loading del POST de sector (botón muestra spinner). */
  creandoSector: boolean;
  setCreandoSector: React.Dispatch<React.SetStateAction<boolean>>;

  /** Ref al Select de sectores — para llamarle .blur() tras crear/
   *  detectar duplicado, cerrando el dropdown automáticamente. */
  sectorSelectRef: React.MutableRefObject<any>;
}

// ─── Componente ─────────────────────────────────────────────────────────

const PropiedadLocationPanel: React.FC<PropiedadLocationPanelProps> = ({
  form,
  paisSelectOptions,
  regionSelectOptions,
  comunaSelectOptions,
  sectorSelectOptions,
  watchPaisModal,
  watchRegionModal,
  watchComunaModal,
  comunaById,
  sectorOptions,
  setSectorOptions,
  creandoSector,
  setCreandoSector,
  sectorSelectRef,
}) => {
  return (
    <div className="flex-container">
      <Form.Item
        label="País"
        name="pais"
        rules={[{ required: true, message: "Selecciona el país" }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona país"
          options={paisSelectOptions}
          allowClear
          onChange={() =>
            // Al cambiar país, limpiamos región/comuna/sector para
            // mantener la coherencia de la cascada (y evitar IDs
            // huérfanos en el form).
            form.setFieldsValue({
              region: undefined,
              comuna: undefined,
              sector: undefined,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="Región"
        name="region"
        rules={[{ required: true, message: "Selecciona la región" }]}
        tooltip={
          watchPaisModal == null
            ? "Elige un país primero para filtrar las regiones."
            : undefined
        }
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder={
            watchPaisModal == null
              ? "Selecciona primero un país"
              : "Selecciona región"
          }
          options={regionSelectOptions}
          allowClear
          onChange={() =>
            form.setFieldsValue({
              comuna: undefined,
              sector: undefined,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="Comuna"
        name="comuna"
        rules={[{ required: true, message: "Selecciona la comuna" }]}
        tooltip={
          watchRegionModal == null
            ? "Elige una región primero para filtrar las comunas."
            : undefined
        }
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder={
            watchRegionModal == null
              ? "Selecciona primero una región"
              : "Selecciona comuna"
          }
          options={comunaSelectOptions}
          allowClear
          onChange={() => form.setFieldsValue({ sector: undefined })}
        />
      </Form.Item>

      <Form.Item
        label="Sector"
        name="sector"
        tooltip="Subdivisión de la comuna (barrio, condominio, edificio). Podés crearlo inline escribiendo el nombre y presionando 'Agregar sector'."
      >
        <Select
          ref={sectorSelectRef}
          showSearch
          optionFilterProp="label"
          placeholder={
            watchComunaModal == null
              ? "Selecciona primero una comuna"
              : "Selecciona o crea sector"
          }
          options={sectorSelectOptions}
          allowClear
          // dropdownRender: añadimos un panel de "Crear nuevo sector"
          // al final del menú. Si no hay comuna seleccionada, lo
          // bloqueamos con mensaje (FK NOT NULL en DB).
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <div
                style={{
                  padding: "4px 12px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  Crear nuevo sector
                </span>
                {watchComunaModal == null ? (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#b35400",
                      lineHeight: 1.4,
                    }}
                  >
                    Seleccioná primero una comuna. El sector queda
                    asociado a esa comuna.
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#888",
                      lineHeight: 1.4,
                    }}
                  >
                    Se creará dentro de{" "}
                    <strong>
                      {comunaById.get(watchComunaModal as number) ??
                        "(comuna)"}
                    </strong>
                    . Luego queda disponible para todas las propiedades
                    de esta comuna.
                  </span>
                )}
                <Input
                  placeholder="Nombre del sector"
                  id="new-sector-name"
                  size="small"
                  disabled={watchComunaModal == null}
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  block
                  loading={creandoSector}
                  disabled={watchComunaModal == null}
                  onClick={async () => {
                    const nameEl = document.getElementById(
                      "new-sector-name",
                    ) as HTMLInputElement | null;
                    const nombre = nameEl?.value.trim();
                    if (!nombre) return;
                    if (watchComunaModal == null) return;

                    // Duplicado local (case/tilde-insensitive): evita
                    // el round-trip si ya existe en la misma comuna.
                    // `strip` normaliza tildes y baja a minúsculas para
                    // que "Ñuñoa" y "nunoa" se consideren iguales.
                    const strip = (s: string) =>
                      s
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "");
                    const dup = sectorOptions.find(
                      (s) =>
                        s.comuna === watchComunaModal &&
                        strip(s.sector) === strip(nombre),
                    );
                    if (dup) {
                      getMsg()?.warning(
                        `"${nombre}" ya existe en esta comuna`,
                      );
                      form.setFieldValue("sector", dup.id);
                      sectorSelectRef.current?.blur();
                      return;
                    }

                    setCreandoSector(true);
                    const res = await propiedadesApi.createSector(
                      nombre,
                      watchComunaModal as number,
                    );
                    setCreandoSector(false);
                    if (!res?.data) return;
                    const nuevo = res.data;
                    setSectorOptions((prev) => [...prev, nuevo]);
                    form.setFieldValue("sector", nuevo.id);
                    if (nameEl) nameEl.value = "";
                    sectorSelectRef.current?.blur();
                  }}
                >
                  Agregar sector
                </Button>
              </div>
            </>
          )}
        />
      </Form.Item>

      {/*
       * Campos de dirección física — todos opcionales. El admin los
       * llena si necesita encontrar la propiedad en el mapa o emitir
       * documentos formales (contratos, facturas).
       */}
      <Form.Item label="Calle" name="calle">
        <Input />
      </Form.Item>

      <Form.Item label="Número" name="numeroCalle">
        <InputNumber style={{ width: "100%" }} min={0} />
      </Form.Item>

      <Form.Item label="Casa o departamento" name="casaODepto">
        <Input placeholder="Ej: Depto" />
      </Form.Item>

      <Form.Item label="Torre o conjunto" name="torreOConjunto">
        <Input />
      </Form.Item>

      <Form.Item label="Piso" name="piso">
        {/* min=-5 permite subterráneos (algunos edificios tienen
            estacionamiento o bodegas en pisos negativos). */}
        <InputNumber style={{ width: "100%" }} min={-5} />
      </Form.Item>

      <Form.Item label="Número departamento/casa" name="numeroDeptoCasa">
        <Input />
      </Form.Item>

      <Form.Item label="Nombre del condominio" name="nombreCondominio">
        <Input />
      </Form.Item>

      <Form.Item label="Estacionamiento" name="estacionamiento">
        <Input />
      </Form.Item>
    </div>
  );
};

export default PropiedadLocationPanel;
